/**
 * Suspend Owner Modal
 * 사업자 사용 중지 모달
 */

import React, { useState } from 'react';
import { Organization } from '../../models';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SuspendOwnerModalProps {
  organization: Organization;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  loading?: boolean;
}

export const SuspendOwnerModal: React.FC<SuspendOwnerModalProps> = ({
  organization,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (reason.trim().length < 5) {
      setError('사용 중지 사유를 5자 이상 입력해주세요.');
      return;
    }

    try {
      await onConfirm(reason.trim());
    } catch (err: any) {
      setError(err.message || '사용 중지 처리 중 오류가 발생했습니다.');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-orange-500 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6" />
            <h2 className="text-lg font-bold">사업자 사용 중지</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Warning */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-orange-800 font-medium">
                  사용 중지 시 오너가 시스템에 접근할 수 없습니다.
                </p>
                <ul className="text-sm text-orange-700 mt-2 list-disc list-inside space-y-1">
                  <li>오너 대시보드 로그인 불가</li>
                  <li>회원 관리 기능 이용 불가</li>
                  <li>좌석/이용권 관리 이용 불가</li>
                  <li>모든 API 호출 차단</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Organization Info */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">대상 사업자</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-semibold text-gray-900">{organization.name}</p>
              {organization.owner_email && (
                <p className="text-sm text-gray-600 mt-1">
                  {organization.owner_email}
                </p>
              )}
              {organization.owner_phone && (
                <p className="text-sm text-gray-600">
                  {organization.owner_phone}
                </p>
              )}
            </div>
          </div>

          {/* Reason Input */}
          <div className="mb-6">
            <label
              htmlFor="suspend-reason"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              사용 중지 사유 <span className="text-red-500">*</span>
            </label>
            <textarea
              id="suspend-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError(null);
              }}
              placeholder="사용 중지 사유를 입력하세요 (예: 미결제, 서비스 약관 위반 등)"
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              최소 5자 이상 입력해주세요. ({reason.length}/5)
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading || reason.trim().length < 5}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>처리 중...</span>
                </>
              ) : (
                <span>사용 중지</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
